import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
    SortAndFilterProps,
    BaseSortAndHeaderSection,
    DesktopSortAndHeaderSection,
} from './SortAndHeaderSection';

import ResponsiveContainer, {
    ContainerType,
} from 'components/responsive-containers/ResponsiveContainer';

import { useRootSelector, FeaturesState } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { UserAuthState } from 'common/features/store/duck/account/duck';

import {
    brandsSearchByPrefix,
    filterByPrefix,
    IFilterByPrefix,
} from 'common/features/store/duck/search-generic/duck';
import { useDispatch, useSelector } from 'react-redux';
import { MobileDialog } from './MobileDialog';
import { createStyle } from 'utils/createStyle';
import { sortOptions } from './variables';
import { useSearchSortOptions } from './hooks';
import { MobileSortDialog } from './MobileSortDialog';

import {
    AlphabetFilter,
    getSearchTerm,
    AThroughZMobileFilterCategory,
} from './AlphabetFilterUtils';

export const NotAuthenticatedFilterView = ({ visible }: { visible: boolean }) => {
    const dispatch = useDispatch<any>();
    const authicatedState = useRootSelector(
        (state: RootState) => state.store.account.userAuthState
    );
    const [currentFilter, setCurrentFilterTo] = useState<string>('All');
    const { sortedBy, setSortedBy, sortOptions } = useSearchSortOptions();
    const appliedTerm = useSelector((state: FeaturesState) => state.store.search.appliedTerm);

    useEffect(() => {
        setCurrentFilterTo(appliedTerm);
    }, [appliedTerm]);

    useEffect(() => {
        const getBrands = (currentFilter: string, sortBy: string) => {
            const [term, prefix_to_filter_by] = getSearchTerm(currentFilter);
            return brandsSearchByPrefix.requestData({
                prefix: prefix_to_filter_by,
                search: term,
                queryType: 'full',
                filter: '((OrganizationId eq null))',
                searchFields: 'Name',
                orderby: sortBy,
                count: true,
            })(dispatch);
        };

        getBrands(currentFilter, sortedBy.value);
    }, [dispatch, currentFilter, sortedBy]);

    // do not show the not-authenticated view if user is logged in
    if (authicatedState === UserAuthState.AUTHENTICATED || !visible) {
        return null;
    }

    return (
        <div className="md:mb-8">
            <NotAuthenticatedSortAndHeaderSection
                currentFilter={currentFilter}
                setFilters={setCurrentFilterTo}
                sortOptions={sortOptions}
                sortedBy={sortedBy}
                setSortedBy={setSortedBy}
            />
            <ResponsiveContainer For={ContainerType.desktop} defaultVisibility>
                <AlphabetFilter currentFilter={currentFilter} applyFilter={setCurrentFilterTo} />
            </ResponsiveContainer>
        </div>
    );
};

interface NotAuthedSortAndHeaderSectionProps
    extends Pick<SortAndFilterProps, 'sortOptions' | 'sortedBy' | 'setSortedBy'> {
    currentFilter: string;
    setFilters: (_: string) => void;
}

const NotAuthenticatedSortAndHeaderSection = ({
    // props for filter
    currentFilter,
    setFilters,

    // props for sort
    sortOptions,
    sortedBy,
    setSortedBy,
}: NotAuthedSortAndHeaderSectionProps) => {
    return (
        <BaseSortAndHeaderSection>
            <ResponsiveContainer For={ContainerType.mobile}>
                <NotAuthenticatedMobileFilterSection
                    currentFilter={currentFilter}
                    sortOptions={sortOptions}
                    sortedBy={sortedBy}
                    setSortedBy={setSortedBy}
                    setFilters={setFilters}
                />
            </ResponsiveContainer>
            <DesktopSortAndHeaderSection
                sortOptions={sortOptions}
                sortedBy={sortedBy}
                setSortedBy={setSortedBy}
            />
        </BaseSortAndHeaderSection>
    );
};

const footerBtnStyle = createStyle(
    'mt-5 w-full py-1 px-10 py-3 rounded-3xl bg-brand text-white font-semibold',
    {
        maxWidth: 280,
    }
);

const fetchBrandsCount = async (opts: IFilterByPrefix) => {
    const response = await filterByPrefix(opts);
    return response.data?.brands.length ?? 0;
};

const NotAuthenticatedMobileFilterSection = ({
    currentFilter,
    sortedBy,
    setFilters,
    setSortedBy,
}: NotAuthedSortAndHeaderSectionProps) => {
    const [filterData, setFilterData] = useState<string>(currentFilter);
    const [resultsCount, setResultsCount] = useState<number>(0);

    const onFilterClicked = (option: string) => {
        setFilterData(option);
    };

    useEffect(() => {
        (async () => {
            const [term, prefix_to_filter_by] = getSearchTerm(filterData);
            const count = await fetchBrandsCount({
                prefix: prefix_to_filter_by,
                search: term,
                queryType: 'full',
                filter: '((OrganizationId eq null))',
                searchFields: 'Name',
                orderby: sortedBy.value,
                count: true,
            });
            setResultsCount(count);
        })();
    }, [filterData, sortedBy]);

    const clearAll = useCallback(() => {
        setFilterData('All');
    }, []);

    const Header = useMemo(
        () => (
            <div>
                <span className="text-xs text-brand underline" onClick={clearAll}>
                    Clear All
                </span>
            </div>
        ),
        [clearAll]
    );

    const onApplyClicked = () => {
        setFilters(filterData);
    };

    const onDialogToggle = (value: boolean) => {
        // CASE: user changed filter but didn't apply it and closed the slide out
        // We should revert to the previous applied filter
        if (value) {
            setFilterData(currentFilter);
        }
    };

    return (
        <div className="block sm:hidden text-right w-full">
            <div className="inline-block flex justify-evenly">
                <MobileDialog
                    btnName="Filter"
                    footer={(onClick: React.MouseEventHandler) => (
                        <button className={footerBtnStyle} onClick={onClick}>
                            Show {resultsCount} Results
                        </button>
                    )}
                    header={Header}
                    styles={{ container: 'border-1 border-l-0' }}
                    onApply={onApplyClicked}
                    onToggle={onDialogToggle}
                >
                    <AThroughZMobileFilterCategory
                        currentFilter={filterData}
                        onFilterClicked={onFilterClicked}
                    />
                </MobileDialog>
                <MobileSortDialog
                    options={sortOptions}
                    sortedByField={sortedBy}
                    onChange={setSortedBy}
                />
            </div>
        </div>
    );
};
